import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Absence } from "../../services/availability/types";
import { formatDateKey, splitAbsences } from "../../utils/availability";
import { todayIsoDate } from "../../utils/age";

type AbsenceListProps = {
  absences: Absence[];
  /** Absent en lecture seule. */
  onEdit?: (absence: Absence) => void;
};

const SHORT_DATE: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "long", year: "numeric" };

const formatPeriod = (absence: Absence) =>
  absence.startDate === absence.endDate
    ? `Le ${formatDateKey(absence.startDate, SHORT_DATE)}`
    : `Du ${formatDateKey(absence.startDate, SHORT_DATE)} au ${formatDateKey(absence.endDate, SHORT_DATE)}`;

type AbsenceRowProps = { absence: Absence; isPast: boolean; isCurrent: boolean; onEdit?: (absence: Absence) => void };

const AbsenceRow: React.FC<AbsenceRowProps> = ({ absence, isPast, isCurrent, onEdit }) => (
  <li
    className={cn(
      "group/row flex items-center gap-3 px-5 py-3 transition-colors",
      isPast ? "text-slate-400" : "hover:bg-emerald-50/40"
    )}
  >
    <i className={cn("bx bx-calendar-x text-xl", isPast ? "text-slate-300" : "text-amber-500")} aria-hidden />
    <div className="min-w-0 flex-1">
      <p className={cn("text-sm font-medium", isPast ? "text-slate-500" : "text-slate-800")}>
        {formatPeriod(absence)}
        {isCurrent && (
          <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            En cours
          </span>
        )}
      </p>
      {absence.reason && <p className="truncate text-xs text-slate-500">{absence.reason}</p>}
    </div>
    {onEdit && (
      <button
        type="button"
        onClick={() => onEdit(absence)}
        title="Modifier l'absence"
        aria-label="Modifier l'absence"
        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 opacity-70 transition-opacity hover:bg-slate-100 group-hover/row:opacity-100"
      >
        <i className="bx bx-edit-alt text-lg" />
      </button>
    )}
  </li>
);

/** Absences à venir en tête ; les passées, grisées, sont repliées par défaut. */
const AbsenceList: React.FC<AbsenceListProps> = ({ absences, onEdit }) => {
  const [showPast, setShowPast] = useState(false);
  const today = todayIsoDate();
  const { upcoming, past } = useMemo(() => splitAbsences(absences, today), [absences, today]);

  return (
    <div>
      {upcoming.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">Aucune absence prévue.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {upcoming.map((absence) => (
            <AbsenceRow
              key={absence.id}
              absence={absence}
              isPast={false}
              isCurrent={absence.startDate <= today}
              onEdit={onEdit}
            />
          ))}
        </ul>
      )}
      {past.length > 0 && (
        <div className="border-t border-slate-100">
          <button
            type="button"
            onClick={() => setShowPast((visible) => !visible)}
            aria-expanded={showPast}
            className="flex w-full items-center gap-2 px-5 py-3 text-left text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            <i className={cn("bx bx-chevron-right text-base transition-transform duration-200", showPast && "rotate-90")} />
            Absences passées ({past.length})
          </button>
          {showPast && (
            <ul className="divide-y divide-slate-100 bg-slate-50/50 animate-in fade-in duration-300">
              {past.map((absence) => (
                <AbsenceRow key={absence.id} absence={absence} isPast isCurrent={false} onEdit={onEdit} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AbsenceList;
